import React, { useState } from 'react';
import { Button, Box } from '@mui/material';
import { TableChart as TableChartIcon, IosShare as IosShareIcon } from '@mui/icons-material';
import StyledModal from '../../NewUI/Components/GenericModal/StyledModal';
import ChartDataTable from './ChartDataTable';
import { actionButton } from '../../NewUI/shared/styleClasses';

interface ChartDataTableModalProps {
  chartData: {
    labels: string[];
    datasets: { label: string; data: number[] }[];
  };
  chartTitle?: string;
}

function chartDataToCsv(chartData: ChartDataTableModalProps['chartData']): string {
  if (!chartData || !chartData.labels || !chartData.datasets) return '';
  const header = ['Label', ...chartData.datasets.map((ds) => ds.label)];
  const rows = chartData.labels.map((label, i) => [
    label,
    ...chartData.datasets.map((ds) => ds.data[i])
  ]);
  const csv = [header, ...rows]
    .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  return csv;
}

const ChartDataTableModal: React.FC<ChartDataTableModalProps> = ({ chartData, chartTitle }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const handleExportCsv = () => {
    const csv = chartDataToCsv(chartData);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${chartTitle ? chartTitle.replace(/\s+/g, '_').toLowerCase() : 'chart_data'}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<TableChartIcon sx={{ color: '#4EC1C1' }} />}
        onClick={handleOpenModal}
        sx={{
          ...actionButton,
          background: '#E6F7F7',
          color: '#4EC1C1',
          '& svg': {
            color: '#4EC1C1'
          },
          '&:hover': {
            background: '#4EC1C1',
            color: '#fff',
            '& .MuiSvgIcon-root': { color: '#fff' }
          }
        }}
      >
        Data Table Preview
      </Button>

      <StyledModal
        isOpen={isModalOpen}
        handleClose={handleCloseModal}
        label="chart-data-table"
        styleOverrides={{
          maxWidth: '1200px',
          width: '95%',
          maxHeight: '800px',
          height: 'auto',
          padding: '40px'
        }}
      >
        <Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>
          <ChartDataTable chartData={chartData} chartTitle={chartTitle} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
            <Button
              variant="outlined"
              startIcon={<IosShareIcon sx={{ color: '#4EC1C1' }} />}
              onClick={handleExportCsv}
              sx={{
                ...actionButton,
                background: '#E6F7F7',
                color: '#4EC1C1',
                '& svg': {
                  color: '#4EC1C1'
                },
                '&:hover': {
                  background: '#4EC1C1',
                  color: '#fff',
                  '& .MuiSvgIcon-root': { color: '#fff' }
                },
                fontWeight: 600,
                fontSize: '15px',
                textTransform: 'none',
                minWidth: 180,
                mb: 1,
                mr: 1
              }}
            >
              Export CSV
            </Button>
          </Box>
        </Box>
      </StyledModal>
    </>
  );
};

export default ChartDataTableModal;
